<!-- Footer.vue -->
<template>
    <footer class="footer">
        <!-- Icon 1 -->
        <div class="icon">
            <router-link :to="{ name: 'MyChat' }">
                <i class="material-icons">
                    forum
                </i>
                <div :class="{ underline: states.colorIcon === 'myChat' }"></div>
            </router-link>
            <div :class="{ selectedName: states.colorIcon === 'myChat' }">צ'אט
                <div v-if="chatTotalCounter > 0" class="notification-circle">
                    {{ chatTotalCounter }}
                </div>
            </div>
        </div>

        <!-- Icon 2 -->
        <div class="icon">
            <router-link :to="{ name: 'MyProfile' }">
                <i class="material-icons">
                    account_circle
                </i>
                <div :class="{ underline: states.colorIcon === 'myProfile' }"></div>
                <div :class="{ selectedName: states.colorIcon === 'myProfile' }">פרופיל</div>
            </router-link>
        </div>

        <!-- Icon 3 -->
        <div class="icon">
            <router-link :to="{ name: 'Home' }" @click="states.showVolunteer = false">
                <i class="material-icons">
                    home
                </i>
                <div :class="{ underline: states.colorIcon === 'home' }"></div>
                <div :class="{ selectedName: states.colorIcon === 'home' }">ראשי</div>
            </router-link>
        </div>
        <!-- Icon 4 -->
        <div class="icon">
            <router-link :to="{ name: 'MyRequests' }">
                <i class="material-icons">
                    person_search
                </i>
                <div :class="{ underline: states.colorIcon === 'myRequests' }"></div>

                <!-- Added circle with the number -->
                <div :class="{ selectedName: states.colorIcon === 'myRequests' }">
                    בקשות
                    <div v-if="reqsUpdateCounter > 0" class="notification-circle">
                        {{ reqsUpdateCounter }}
                    </div>
                </div>
            </router-link>
        </div>

        <!-- Icon 5 -->
        <div class="icon">
            <router-link :to="{ name: 'MyVolunteering' }">
                <i class="material-icons">
                    diversity_3
                </i>
                <div :class="{ underline: states.colorIcon === 'myVolunteering' }"></div>
                <div :class="{ selectedName: states.colorIcon === 'myVolunteering' }">
                    התנדבויות
                    <div v-if="volUpdateCounter > 0" class="notification-circle">
                        {{ volUpdateCounter }}
                    </div>
                </div>
            </router-link>
        </div>
    </footer>
</template>
<script setup>
import { states } from "@/main_store";
import { collection, query, where, onSnapshot, doc, getDocs, getDoc, updateDoc } from "firebase/firestore";
import { db } from '@/firebase/config'
import { ref, onBeforeUnmount, computed, watchEffect } from 'vue'
import { fetchVolData } from "@/views/views_functions/fetchVolData";

let reqData
let volData
let cleanupFunctions = []

watchEffect(() => {
    if (states.value.user?.uid) {
        const myVolRef = collection(db, `users/${states.value.user.uid}/my-volunteering`);
        const myReqRef = collection(db, `users/${states.value.user.uid}/my-requests`);

        const myVolListenerUnsubscribe = onSnapshot(myVolRef, (snapshot) => {
            fetchMyVolunteering();
        });

        const myReqListenerUnsubscribe = onSnapshot(myReqRef, (snapshot) => {
            fetchMyRequests();
        });

        cleanupFunctions.push(myVolListenerUnsubscribe, myReqListenerUnsubscribe);

        const volsQuery = query(collection(db, `users/${states.value.user.uid}/my-volunteering`));
        const unsubscribeMatchedVols = onSnapshot(volsQuery, async (snapshot) => {
            const checkedDocuments = new Set();
            const today = new Date().toISOString().split('T')[0];

            for (const item of snapshot.docs) {
                const volData = item.data();

                if (volData.unseenUpdate && !checkedDocuments.has(volData.uid) && volData.requestId) {
                    // Validate against actual volunteer-requests collection
                    try {
                        const reqRef = doc(db, 'volunteer-requests', volData.requestId);
                        const reqDoc = await getDoc(reqRef);

                        if (reqDoc.exists()) {
                            const reqData = reqDoc.data();
                            // Only count if request exists, is not canceled, and date hasn't passed
                            if (reqData.status !== 'canceled' && reqData.date >= today) {
                                checkedDocuments.add(volData.uid);
                            } else {
                                // Clear unseenUpdate for past/canceled requests
                                const myVolRef = doc(db, `users/${states.value.user.uid}/my-volunteering`, volData.uid);
                                await updateDoc(myVolRef, { unseenUpdate: false });
                            }
                        } else {
                            // Request was deleted, clear unseenUpdate
                            const myVolRef = doc(db, `users/${states.value.user.uid}/my-volunteering`, volData.uid);
                            await updateDoc(myVolRef, { unseenUpdate: false });
                        }
                    } catch (error) {
                        console.error('Error validating volunteer request:', error);
                    }
                }
            }
            volUpdateCounter.value = checkedDocuments.size
        });

        const reqsQuery = query(collection(db, `users/${states.value.user.uid}/my-requests`));
        const unsubscribeMatchedReqs = onSnapshot(reqsQuery, async (snapshot) => {
            const checkedDocuments = new Set();
            const today = new Date().toISOString().split('T')[0];

            for (const item of snapshot.docs) {
                const requestData = item.data();
                if (requestData.unseenUpdate && !checkedDocuments.has(requestData.uid) && requestData.requestId) {
                    // Validate against actual volunteer-requests collection
                    try {
                        const reqRef = doc(db, 'volunteer-requests', requestData.requestId);
                        const reqDoc = await getDoc(reqRef);

                        if (reqDoc.exists()) {
                            const reqData = reqDoc.data();
                            // Only count if request exists, is not canceled, and date hasn't passed
                            if (reqData.status !== 'canceled' && reqData.date >= today) {
                                checkedDocuments.add(requestData.uid);
                            } else {
                                // Clear unseenUpdate for past/canceled requests
                                const myReqRef = doc(db, `users/${states.value.user.uid}/my-requests`, requestData.uid);
                                await updateDoc(myReqRef, { unseenUpdate: false });
                            }
                        } else {
                            // Request was deleted, clear unseenUpdate
                            const myReqRef = doc(db, `users/${states.value.user.uid}/my-requests`, requestData.uid);
                            await updateDoc(myReqRef, { unseenUpdate: false });
                        }
                    } catch (error) {
                        console.error('Error validating request:', error);
                    }
                }
            }
            reqsUpdateCounter.value = checkedDocuments.size
        });

        cleanupFunctions.push(unsubscribeMatchedVols, unsubscribeMatchedReqs);
    }
});

const unseenGroupMessagesCount = async () => {
    // Subscribe to messages from each req group and update unread message count
    states.value.groupReqList.forEach((request) => {
        const userDocRef = doc(db, `group_chat/${request.uid}/users`, states.value.user.uid);
        const userUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const lastSeen = docSnap.data().last_seen;

                const handleMessagesQuery = (lastSeenTimestamp) => {
                    const messagesQuery = query(
                        collection(db, `group_chat/${request.uid}/messages`),
                        where("createdAt", ">", lastSeenTimestamp)
                    );

                    if (request.messagesUnsubscribe) {
                        request.messagesUnsubscribe();
                    }
                    request.messagesUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
                        // Update field with unread message count for this request
                        const visibleDocs = snapshot.docs.filter(doc => !doc.data().hide || doc.data().sender !== states.value.user.uid);
                        request.unreadMessageCount = visibleDocs.length;
                    }, (error) => {
                        if (request.messagesUnsubscribe) request.messagesUnsubscribe();
                        request.unreadMessageCount = 0;
                    });
                };

                handleMessagesQuery(lastSeen);
            }
        }, (error) => {
            userUnsubscribe();
            request.unreadMessageCount = 0;
        });
        request.unsubscribe = userUnsubscribe;
    });
    // Subscribe to messages from each vol group and update unread message count
    states.value.groupVolList.forEach((request) => {
        const userDocRef = doc(db, `group_chat/${request.uid}/users`, states.value.user.uid);
        const userUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const lastSeen = docSnap.data().last_seen;

                const handleMessagesQuery = (lastSeenTimestamp) => {
                    const messagesQuery = query(
                        collection(db, `group_chat/${request.uid}/messages`),
                        where("createdAt", ">", lastSeenTimestamp)
                    );

                    if (request.messagesUnsubscribe) {
                        request.messagesUnsubscribe();
                    }
                    request.messagesUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
                        // Update field with unread message count for this request
                        const visibleDocs = snapshot.docs.filter(doc => !doc.data().hide || doc.data().sender !== states.value.user.uid);
                        request.unreadMessageCount = visibleDocs.length;
                    }, (error) => {
                        if (request.messagesUnsubscribe) request.messagesUnsubscribe();
                        request.unreadMessageCount = 0;
                    });
                };

                handleMessagesQuery(lastSeen);
            }
        }, (error) => {
            userUnsubscribe();
            request.unreadMessageCount = 0;
        });
        request.unsubscribe = userUnsubscribe;
    });
}

const showGroupChatData = async () => {
    if (reqData?.volunteeringRequestsData) { states.value.groupReqList = reqData.volunteeringRequestsData }
    if (volData?.volunteeringRequestsData) { states.value.groupVolList = volData.volunteeringRequestsData }
    states.value.groupReqList.forEach((doc) => {
        if (doc.unsubscribe) {
            doc.unsubscribe();
        }
    });

    states.value.groupVolList.forEach((doc) => {
        if (doc.unsubscribe) {
            doc.unsubscribe();
        }
    });
    unseenGroupMessagesCount()
}

const groupReqUnreadCounter = computed(() => {
    let count = 0
    states.value.groupReqList.forEach((creator) => {
        if (creator.unreadMessageCount > 0) { count++ }
    })
    return count
})

const groupVolUnreadCounter = computed(() => {
    let count = 0
    states.value.groupVolList.forEach((vol) => {
        if (vol.unreadMessageCount > 0) { count++ }
    })
    return count
})

const creatorUnreadCounter = computed(() => {
    let count = 0
    states.value.creatorsArray.forEach((creator) => {
        if (creator.unreadMessageCount > 0) { count++ }
    })
    return count
})

const volunteerUnreadCounter = computed(() => {
    let count = 0
    states.value.volunteersArray.forEach((vol) => {
        if (vol.unreadMessageCount > 0) { count++ }
    })
    return count
})

const chatTotalCounter = computed(() => {
    return groupReqUnreadCounter.value + groupVolUnreadCounter.value + creatorUnreadCounter.value + volunteerUnreadCounter.value;
});

const fetchUsersById = async (ids) => {
    try {
        const usersRef = collection(db, "users");

        const users = [];

        for (const id of ids) {
            const q = query(usersRef, where("uid", "==", id));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                users.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
        }
        return users;
    } catch (error) {
        console.log(error)
    }
};

const getVolunteersArray = async (data) => {
    const volunteersSet = new Set();
    for (const request of data) {
        if (Array.isArray(request.volunteers)) {
            for (const volunteer of request.volunteers) {
                volunteersSet.add(volunteer);
            }
        }
    }
    const volunteersArray = Array.from(volunteersSet);
    return (volunteersArray);
}

const getCreatorArray = async (data) => {
    const creatorSet = new Set();
    for (const request of data) {
        creatorSet.add(request.creator_id);
    }
    const creatorArray = Array.from(creatorSet);
    return (creatorArray);
}

const unsubscribeCreatorsListeners = async () => {
    states.value.creatorsArray.forEach((doc) => {
        doc.unsubscribe();
    });
}

const unsubscribeVolListeners = async () => {
    states.value.volunteersArray.forEach((doc) => {
        doc.unsubscribe();
    });
}

const fetchMyVolunteering = async () => {
    try {
        await unsubscribeCreatorsListeners()

        // volData has two arrays: 1. myVolunteeringData, which is the references to 
        // requests I volunteered for and is in my-volunteering subcollection. 
        // 2. volunteeringRequestData which is requests I volunteered for, and is in 
        // volunteer-request collection. 
        volData = await fetchVolData('my-volunteering', states.value.user.uid);
        if (volData.myVolunteeringData) {
            for (const vol of volData.myVolunteeringData) {
                if (vol.unseenUpdate) {
                    const reqRef = doc(db, 'volunteer-requests', `${vol.requestId}`);
                    const reqDoc = await getDoc(reqRef)
                    if (reqDoc.exists()) {
                        if (reqDoc.data().date < new Date().toISOString().split('T')[0]) {
                            const myReqRef = doc(db, `users/${states.value.user.uid}/my-volunteering`, `${vol.uid}`);
                            await updateDoc(myReqRef, {
                                'unseenUpdate': false
                            });
                        }
                    }
                }
            }
        }
        if (volData.volunteeringRequestsData) {
            states.value.volunteeringRequestData = volData.volunteeringRequestsData
            if (volData) {
                // get id's of creators of requests I volunteered for
                const creators = await getCreatorArray(volData.volunteeringRequestsData);
                // get docs of creators of requests I volunteered for
                states.value.creatorsArray = await fetchUsersById(creators);

                // Initialize unread message count for each creator
                states.value.creatorsArray.forEach((doc) => {
                    doc.unreadMessageCount = 0;
                });

                // Subscribe to messages from each creator and update unread message count
                states.value.creatorsArray.forEach((creator) => {
                    const unsubscribe = onSnapshot(
                        query(collection(db, `chat/${creator.uid}/messages`), where("recipient", "==", states.value.user.uid), where("seen", "==", false)
                        ), (snapshot) => {
                            // Update the unread message count for this creator
                            const visibleDocs = snapshot.docs.filter(doc => !doc.data().hide);
                            creator.unreadMessageCount = visibleDocs.length;
                        });
                    creator.unsubscribe = unsubscribe;
                });
                showGroupChatData()
            } else {
                console.log("error fetching vol data");
            }
        }
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

// fetchMyVolunteering();

const fetchMyRequests = async () => {
    try {
        await unsubscribeVolListeners()

        // reqData has two arrays: 1. myVolunteeringData, which is the references to 
        // requests I created and is in my-requests subcollection. 
        // 2. volunteeringRequestData which is requests I created, and is in 
        // volunteer-request collection. 
        reqData = await fetchVolData('my-requests', states.value.user.uid);
        if (reqData.myVolunteeringData) {
            for (const req of reqData.myVolunteeringData) {
                if (req.unseenUpdate) {
                    const reqRef = doc(db, 'volunteer-requests', `${req.requestId}`);
                    const reqDoc = await getDoc(reqRef)
                    if (reqDoc.exists()) {
                        if (reqDoc.data().date < new Date().toISOString().split('T')[0]) {
                            const myReqRef = doc(db, `users/${states.value.user.uid}/my-requests`, `${req.uid}`);
                            await updateDoc(myReqRef, {
                                'unseenUpdate': false
                            });
                        }
                    }
                }
            }
        }

        if (reqData.volunteeringRequestsData) {
            // get id's of volunteers to my requests
            const volunteers = await getVolunteersArray(reqData.volunteeringRequestsData);

            // get docs of volunteers to my requests
            states.value.volunteersArray = await fetchUsersById(volunteers);

            // Initialize unread message count for each volunteer
            states.value.volunteersArray.forEach((doc) => {
                doc.unreadMessageCount = 0;
            });

            // Subscribe to messages from each volunteer and update unread message count
            states.value.volunteersArray.forEach((volunteer) => {
                const unsubscribe = onSnapshot(
                    query(collection(db, `chat/${volunteer.uid}/messages`), where("recipient", "==", states.value.user.uid), where("seen", "==", false)
                    ), (snapshot) => {
                        // Update field with unread message count for this volunteer
                        const visibleDocs = snapshot.docs.filter(doc => !doc.data().hide);
                        volunteer.unreadMessageCount = visibleDocs.length;
                    });
                volunteer.unsubscribe = unsubscribe;
            });
            showGroupChatData()
        } else {
            console.log("error fetching vol data");
        }
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

// fetchMyRequests();

// *******

const reqsUpdateCounter = ref(0)
const volUpdateCounter = ref(0)

onBeforeUnmount(() => {
    cleanupFunctions.forEach(unsubscribe => unsubscribe());
    cleanupFunctions = [];
    states.value.groupReqList.forEach((doc) => {
        if (doc.unsubscribe) {
            doc.unsubscribe();
        }
    });

    states.value.groupVolList.forEach((doc) => {
        if (doc.unsubscribe) {
            doc.unsubscribe();
        }
    });
})
</script>
<style scoped>
.footer {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    width: calc(100% - 10px);
    height: 80px;
    user-select: none;
    color: #fafafa;
    padding: 5px;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
    max-width: 400px;
}

.notification-circle {
    position: absolute;
    top: 0;
    right: 0;
    background-color: var(--dark-red);
    /* Choose your desired background color */
    color: #ffffff;
    /* Choose your desired text color */
    border-radius: 50%;
    padding: 5px;
    font-size: 12px;
    font-weight: bold;
    height: 27px;
    width: 27px;
}

/* @media only screen and (max-width: 600px) {
    .footer {
        height: 110px;
    }
} */


::selection {
    background-color: transparent;
    color: inherit;
}

.icon {
    position: relative;
    justify-content: center;
    text-align: center;
    color: rgb(64, 115, 255);
    padding: 5px;
    height: 100%;
    width: calc(100% / 5);
    /* overflow: hidden; */
    font-size: 14px;
}

.icon i {
    font-size: 26px;
    transition: 0.3s;
    margin-bottom: 8px;
}

.selectedIcon {
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    border-radius: 5px;
    background: rgb(64, 115, 255);
}

.selectedName {
    color: rgb(69, 69, 243);
    font-weight: 700;
}

.underline {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 2px;
    background-color: #c9d8e4;
    z-index: 1;
    margin-top: -1px;
}
</style>